import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import {
  collection, query, orderBy, limit, startAfter, getDocs,
  updateDoc, doc, serverTimestamp, getDoc,
} from "firebase/firestore";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import {
  MessageSquare, AlertTriangle, CheckCircle, Clock,
  User, Send, Phone, MapPin, RefreshCw, CornerDownLeft, Printer,
} from "lucide-react";
import jsPDF from "jspdf";
import "jspdf-autotable";

const NOTES_PAGE_SIZE = 20;

function getNotesDateRange(filter, customFrom, customTo) {
  const now   = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (filter === "today") {
    return { from: today, to: null };
  }
  if (filter === "yesterday") {
    const yest = new Date(today); yest.setDate(yest.getDate() - 1);
    return { from: yest, to: today };
  }
  if (filter === "this_week") {
    const weekStart = new Date(today);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    return { from: weekStart, to: null };
  }
  if (filter === "this_month") {
    return { from: new Date(today.getFullYear(), today.getMonth(), 1), to: null };
  }
  if (filter === "custom" && customFrom) {
    const from = new Date(customFrom);
    const to   = customTo ? new Date(new Date(customTo).setHours(23, 59, 59, 999)) : null;
    return { from, to };
  }
  return { from: null, to: null };
}

function NotesInbox({ companyId, onShowOnMap }) {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const [notes,           setNotes]           = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [loadingMore,     setLoadingMore]     = useState(false);
  const [hasMore,         setHasMore]         = useState(true);
  const [notesLastDoc,    setNotesLastDoc]    = useState(null);
  const [noteFilter,      setNoteFilter]      = useState("all");   // all | urgent | normal
  const [senderFilter,    setSenderFilter]    = useState("all");   // all | vendor | operator | supervisor
  const [dateFilter,      setDateFilter]      = useState("all");   // all | today | yesterday | this_week | this_month | custom
  const [customDateFrom,  setCustomDateFrom]  = useState("");
  const [customDateTo,    setCustomDateTo]    = useState("");
  const [companyData,     setCompanyData]     = useState(null);
  const [error,           setError]           = useState("");
  const [replyTarget,     setReplyTarget]     = useState(null);
  const [replyText,       setReplyText]       = useState("");
  const [replySaving,     setReplySaving]     = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadNotes(true); }, [companyId]);
  useEffect(() => {
    if (!companyId) return;
    async function loadCompany() {
      try {
        const snap = await getDoc(doc(db, "companies", companyId));
        if (snap.exists()) setCompanyData({ id: snap.id, ...snap.data() });
      } catch (err) {
        console.error("Company load error:", err.message);
      }
    }
    loadCompany();
  }, [companyId]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadNotes(true); }, [dateFilter, customDateFrom, customDateTo]);

  async function loadNotes(reset = false) {
    if (!reset && loadingMore) return;
    reset ? setLoading(true) : setLoadingMore(true);
    setError("");
    try {
      // Fetch a large page when date filter active (JS-side filtering); otherwise paginate normally
      const { from: dateFrom, to: dateTo } = getNotesDateRange(dateFilter, customDateFrom, customDateTo);
      const hasDateFilter = dateFrom !== null;
      const pageSize = hasDateFilter ? 500 : NOTES_PAGE_SIZE;

      let q = query(
        collection(db, "companies", companyId, "notes"),
        orderBy("created_at", "desc"),
        limit(pageSize)
      );
      if (!reset && notesLastDoc && !hasDateFilter) {
        q = query(
          collection(db, "companies", companyId, "notes"),
          orderBy("created_at", "desc"),
          startAfter(notesLastDoc),
          limit(pageSize)
        );
      }
      const snap     = await getDocs(q);
      let newNotes   = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Apply date filter in JS to avoid composite index requirement
      if (hasDateFilter) {
        newNotes = newNotes.filter(n => {
          const ts = n.created_at?.toDate ? n.created_at.toDate() : (n.created_at ? new Date(n.created_at) : null);
          if (!ts) return false;
          if (dateFrom && ts < dateFrom) return false;
          if (dateTo   && ts > dateTo)   return false;
          return true;
        });
      }

      const lastSnap = snap.docs[snap.docs.length - 1] || null;

      if (reset) {
        setNotes(newNotes);
      } else {
        setNotes(prev => [...prev, ...newNotes]);
      }
      setNotesLastDoc(lastSnap);
      // When date filter active, no pagination (all fetched at once)
      setHasMore(!hasDateFilter && snap.docs.length === pageSize);
      fetchMissingPhones(newNotes);
    } catch (err) {
      setError("Failed to load notes: " + err.message + (err.code ? ` (${err.code})` : ""));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }

  async function fetchMissingPhones(notesList) {
    // Find notes missing client_phone but having a client_id
    const missing = notesList.filter(n => !n.client_phone && n.client_id);
    if (missing.length === 0) return;
    try {
      const fetched = await Promise.all(
        missing.map(n => getDoc(doc(db, "companies", companyId, "clients", n.client_id)))
      );
      const phoneMap = {};
      fetched.forEach((snap, i) => {
        if (snap.exists()) phoneMap[missing[i].id] = snap.data().phone || "";
      });
      setNotes(prev => prev.map(n =>
        phoneMap[n.id] !== undefined ? { ...n, client_phone: phoneMap[n.id] } : n
      ));
    } catch (err) {
      // Silent — phone is informational, don't block the UI
      console.error("fetchMissingPhones error:", err.message);
    }
  }

  // Mark as read when card is clicked (only if unread and not yet replied)
  async function markRead(note) {
    if (note.read || note.reply) return;
    try {
      await updateDoc(doc(db, "companies", companyId, "notes", note.id), { read: true });
      setNotes(prev => prev.map(n => n.id === note.id ? { ...n, read: true } : n));
    } catch (err) {
      setError("Error: " + err.message);
    }
  }

  async function sendReply(note) {
    if (!replyText.trim()) return;
    setReplySaving(true);
    try {
      await updateDoc(doc(db, "companies", companyId, "notes", note.id), {
        reply:      replyText.trim(),
        replied_at: serverTimestamp(),
        replied_by: currentUser.displayName || currentUser.email,
        read:       true,
      });
      setNotes(prev => prev.map(n =>
        n.id === note.id
          ? { ...n, reply: replyText.trim(), read: true,
              replied_by: currentUser.displayName || currentUser.email }
          : n
      ));
      setReplyTarget(null);
      setReplyText("");
    } catch (err) {
      setError("Error sending reply: " + err.message);
    }
    setReplySaving(false);
  }

  function generateRestockPDF(note, company) {
    try {
      const doc = new jsPDF();

      // Header — yellow bar
      doc.setFillColor(245, 158, 11);
      doc.rect(0, 0, 210, 35, "F");

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text(company?.name || companyId, 105, 15, { align: "center" });

      doc.setFontSize(12);
      doc.text("RESTOCK REQUEST FORM", 105, 25, { align: "center" });

      // Request details
      doc.setTextColor(17, 24, 39);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");

      let y = 50;

      doc.setFont("helvetica", "bold");
      doc.text("Request Details", 20, y);
      y += 8;

      const noteDate = note.created_at?.toDate ? note.created_at.toDate() : new Date();
      doc.setFont("helvetica", "normal");
      doc.text(`Request Date: ${noteDate.toLocaleDateString()}`, 20, y); y += 7;
      doc.text(`Request Time: ${noteDate.toLocaleTimeString()}`, 20, y); y += 7;
      doc.text(`Vendor: ${note.sent_by_name || note.sent_by || "Unknown"}`, 20, y); y += 7;
      if (note.message) {
        const msgLines = doc.splitTextToSize(`Request: ${note.message}`, 170);
        doc.text(msgLines, 20, y);
        y += msgLines.length * 6 + 3;
      }
      y += 5;

      // Table — message as single row (restock notes store free-text, not structured items)
      doc.autoTable({
        startY: y,
        head: [["#", "Product", "Unit", "Requested Qty", "Current Stock"]],
        body: [[1, note.message || "", "", "", ""]],
        styles: { fontSize: 9, cellPadding: 4 },
        headStyles: { fillColor: [245, 158, 11], textColor: [255, 255, 255], fontStyle: "bold" },
        alternateRowStyles: { fillColor: [249, 250, 251] },
      });

      y = (doc.lastAutoTable?.finalY ?? y) + 20;
      if (y > 240) { doc.addPage(); y = 20; }

      // Signature section
      doc.setDrawColor(156, 163, 175);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);

      doc.text("Vendor Signature:", 20, y);
      doc.line(20, y + 15, 90, y + 15);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text("Name & Signature", 20, y + 20);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text("Admin Approval:", 120, y);
      doc.line(120, y + 15, 190, y + 15);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text("Name, Signature & Date", 120, y + 20);

      doc.setFontSize(8);
      doc.setTextColor(107, 114, 128);
      doc.text(`Generated by Pinpoint  |  ${new Date().toLocaleString()}`, 105, 285, { align: "center" });

      doc.save(`restock-${note.id || Date.now()}.pdf`);
      console.log("[RESTOCK PDF] Generated successfully");
    } catch (err) {
      console.error("[RESTOCK PDF] Generation failed:", err.message, err.stack);
      alert(`Could not generate PDF: ${err.message}`);
    }
  }

  function formatTime(ts) {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString([], {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  }

  // Status: mutually exclusive — replied > read > unread
  function getStatus(note) {
    if (note.reply) return "replied";
    if (note.read)  return "read";
    return "unread";
  }

  // Apply priority and sender filters in-memory on the loaded notes
  const filteredNotes = notes.filter(n => {
    const matchPriority = noteFilter === "all" || n.priority === noteFilter;
    const matchSender   = senderFilter === "all" || n.sent_by_role === senderFilter;
    return matchPriority && matchSender;
  });

  // Sort: Unread Urgent → Unread Normal → Read → Replied (always bottom)
  const sorted = [...filteredNotes].sort((a, b) => {
    const sA = getStatus(a);
    const sB = getStatus(b);
    // Replied always at bottom — score 0, regardless of urgency
    const score = (n, s) => {
      if (s === "replied") return 0;
      return (n.priority === "urgent" ? 2 : 0) + (s === "unread" ? 1 : 0) + 1; // +1 so non-replied base is ≥1
    };
    const scoreA = score(a, sA);
    const scoreB = score(b, sB);
    if (scoreB !== scoreA) return scoreB - scoreA;
    const tA = a.created_at?.toDate ? a.created_at.toDate() : new Date(a.created_at || 0);
    const tB = b.created_at?.toDate ? b.created_at.toDate() : new Date(b.created_at || 0);
    return tB - tA;
  });

  // Unread count across ALL loaded notes (before filters)
  const unreadCount = notes.filter(n => getStatus(n) === "unread").length;

  // Role badge component
  function RoleBadge({ role }) {
    if (role === "operator")   return <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">{t("notes_role_operator")}</span>;
    if (role === "vendor")     return <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700">{t("notes_role_vendor")}</span>;
    if (role === "supervisor") return <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700">{t("notes_role_supervisor")}</span>;
    return <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{role || t("notes_role_staff")}</span>;
  }

  if (loading) return (
    <div className="bg-white rounded-xl shadow-sm p-12 text-center">
      <p className="text-gray-400 text-sm">{t("notes_loading")}</p>
    </div>
  );

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-6 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-purple-600" />
            {t("notes_title")}
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {unreadCount}
              </span>
            )}
          </h2>
          <p className="text-gray-500 text-sm mt-1">{t("notes_subtitle")}</p>
        </div>
        <button onClick={loadNotes}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 px-3 py-1.5 rounded-lg transition hover:bg-gray-50">
          <RefreshCw className="w-3.5 h-3.5" />
          {t("notes_refresh")}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />{error}
        </div>
      )}

      {/* Filter tabs — date + priority + sender role */}
      <div className="bg-white rounded-xl shadow-sm p-4 space-y-2">
        {/* Date filter */}
        <div className="flex gap-2 flex-wrap items-center">
          <span className="text-xs font-semibold text-gray-400 mr-1">{t("notes_date_label")}</span>
          {[
            { id: "all",        label: t("notes_all_time")   },
            { id: "today",      label: t("notes_today")      },
            { id: "yesterday",  label: t("notes_yesterday")  },
            { id: "this_week",  label: t("notes_this_week")  },
            { id: "this_month", label: t("notes_this_month") },
            { id: "custom",     label: t("notes_custom")     },
          ].map(f => (
            <button key={f.id}
              onClick={() => setDateFilter(f.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                dateFilter === f.id
                  ? "bg-indigo-500 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}>
              {f.label}
            </button>
          ))}
        </div>
        {dateFilter === "custom" && (
          <div className="flex gap-2 flex-wrap items-center pt-1">
            <span className="text-xs font-semibold text-gray-400">{t("notes_from")}</span>
            <input type="date" value={customDateFrom} onChange={e => setCustomDateFrom(e.target.value)}
              className="border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-400" />
            <span className="text-xs font-semibold text-gray-400">{t("notes_to")}</span>
            <input type="date" value={customDateTo} onChange={e => setCustomDateTo(e.target.value)}
              className="border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-400" />
          </div>
        )}
        {/* Priority filter */}
        <div className="flex gap-2 flex-wrap items-center">
          <span className="text-xs font-semibold text-gray-400 mr-1">{t("notes_priority_label")}</span>
          {[
            { id: "all",    label: t("notes_all_notes") },
            { id: "urgent", label: t("notes_urgent") },
            { id: "normal", label: t("notes_normal") },
          ].map(f => (
            <button key={f.id}
              onClick={() => setNoteFilter(f.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                noteFilter === f.id
                  ? "bg-yellow-500 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}>
              {f.label}
            </button>
          ))}
        </div>
        {/* Sender filter */}
        <div className="flex gap-2 flex-wrap items-center">
          <span className="text-xs font-semibold text-gray-400 mr-1">{t("notes_sender_label")}</span>
          {[
            { id: "all",        label: t("notes_all_senders") },
            { id: "vendor",     label: t("notes_vendors") },
            { id: "operator",   label: t("notes_operators") },
            { id: "supervisor", label: t("notes_supervisors") },
          ].map(f => (
            <button key={f.id}
              onClick={() => setSenderFilter(f.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                senderFilter === f.id
                  ? "bg-blue-500 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}>
              {f.label}
            </button>
          ))}
        </div>
        {(noteFilter !== "all" || senderFilter !== "all" || dateFilter !== "all") && (
          <p className="text-xs text-gray-400">
            {t("notes_showing")} {sorted.length} {t("notes_of")} {notes.length} {t("notes_loaded")}
          </p>
        )}
      </div>

      {/* Empty state */}
      {sorted.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-400 text-sm">{t("notes_empty")}</p>
        </div>
      )}

      {/* Note cards — sorted and filtered in-memory */}
      {sorted.length === 0 && notes.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <MessageSquare className="w-8 h-8 text-gray-200 mx-auto mb-2" />
          <p className="text-gray-400 text-sm">{t("notes_no_match")}</p>
        </div>
      )}

      {sorted.map((note) => {
        const status    = getStatus(note);
        const isUrgent  = note.priority === "urgent";
        const isReplying = replyTarget === note.id;

        // Left border by status/urgency
        const borderColor =
          isUrgent            ? "border-l-red-500"    :
          status === "unread" ? "border-l-yellow-400" :
          status === "replied"? "border-l-green-400"  :
                                "border-l-gray-200";

        // Card tint for unread
        const cardBg = status === "unread" ? "bg-yellow-50/40" : "bg-white";

        return (
          <div key={note.id}
            onClick={() => markRead(note)}
            className={`${cardBg} rounded-xl shadow-sm overflow-hidden border-l-4 ${borderColor} cursor-pointer`}>

            <div className="p-5 space-y-3">

              {/* ── Badges row ── */}
              <div className="flex items-center gap-2 flex-wrap">
                {isUrgent ? (
                  <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded-full">
                    <AlertTriangle className="w-3 h-3" /> {t("notes_badge_urgent")}
                  </span>
                ) : (
                  <span className="bg-gray-100 text-gray-500 text-xs font-medium px-2 py-0.5 rounded-full">
                    {t("notes_badge_normal")}
                  </span>
                )}
                {/* Status — only ONE shows at a time */}
                {status === "unread" && (
                  <span className="inline-flex items-center gap-1 bg-yellow-100 text-yellow-700 text-xs font-bold px-2 py-0.5 rounded-full">
                    ● {t("notes_badge_unread")}
                  </span>
                )}
                {status === "read" && (
                  <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-400 text-xs font-medium px-2 py-0.5 rounded-full">
                    <CheckCircle className="w-3 h-3" /> {t("notes_badge_read")}
                  </span>
                )}
                {status === "replied" && (
                  <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded-full">
                    <CheckCircle className="w-3 h-3" /> {t("notes_badge_replied")}
                  </span>
                )}
              </div>

              {/* ── Client name + phone + Show on Map ── */}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-base font-bold text-gray-800 leading-tight">
                    {note.client_name || "—"}
                  </p>
                  {note.client_phone && (
                    <p className="flex items-center gap-1.5 text-sm text-gray-500 mt-1">
                      <Phone className="w-3.5 h-3.5 shrink-0 text-gray-400" />
                      {note.client_phone}
                    </p>
                  )}
                </div>
                {onShowOnMap && note.client_location?.lat && note.client_location?.lng && (
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      onShowOnMap({
                        id:       note.client_id,
                        name:     note.client_name,
                        phone:    note.client_phone,
                        location: note.client_location,
                      });
                    }}
                    className="shrink-0 flex items-center gap-1.5 text-xs font-semibold text-blue-600 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-lg hover:bg-blue-100 transition">
                    <MapPin className="w-3.5 h-3.5" />
                    {t("notes_show_map")}
                  </button>
                )}
              </div>

              {/* ── Full message ── */}
              <p className="text-sm text-gray-700 leading-relaxed">{note.message}</p>

              {/* ── Sender + date ── */}
              <div className="flex items-center gap-2 flex-wrap text-xs text-gray-400">
                <User className="w-3.5 h-3.5 shrink-0" />
                <span className="font-medium text-gray-600">{note.sent_by_name || "—"}</span>
                {(() => { const R = RoleBadge; return <R role={note.sent_by_role} />; })()}
                <span className="flex items-center gap-1 ml-auto">
                  <Clock className="w-3.5 h-3.5" />
                  {formatTime(note.created_at)}
                </span>
              </div>

              {/* ── Existing reply block ── */}
              {note.reply && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                  <div className="flex items-center gap-1 mb-1 flex-wrap">
                    <CheckCircle className="w-3.5 h-3.5 text-green-600 shrink-0" />
                    <span className="text-xs font-bold text-green-700">{t("notes_admin_reply")}</span>
                    {note.replied_by && (
                      <span className="text-xs text-green-600">— {note.replied_by}</span>
                    )}
                    {note.replied_at && (
                      <span className="text-xs text-green-500 ml-auto">{formatTime(note.replied_at)}</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-700">{note.reply}</p>
                </div>
              )}

              {/* ── Reply actions (stop propagation so card click doesn't fire) ── */}
              <div onClick={e => e.stopPropagation()}>
                {isReplying ? (
                  <div className="space-y-2 pt-1">
                    <input
                      type="text"
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder={t("notes_reply_ph")} />
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setReplyTarget(null); setReplyText(""); }}
                        className="border border-gray-200 text-gray-600 text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-gray-50 transition">
                        {t("notes_cancel_reply")}
                      </button>
                      <button
                        onClick={() => sendReply(note)}
                        disabled={replySaving || !replyText.trim()}
                        className="bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition">
                        {replySaving
                          ? <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                          : <Send className="w-3.5 h-3.5" />
                        }
                        {replySaving ? t("notes_sending") : t("notes_send")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 pt-1 flex-wrap">
                    {/* Mark as Read button — only shown for unread notes */}
                    {status === "unread" && (
                      <button
                        onClick={() => markRead(note)}
                        className="text-xs font-medium text-purple-600 bg-purple-50 border border-purple-200 px-3 py-1.5 rounded-lg hover:bg-purple-100 transition flex items-center gap-1">
                        <CheckCircle className="w-3.5 h-3.5" />
                        {t("notes_mark_read")}
                      </button>
                    )}
                    {/* Reply button */}
                    <button
                      onClick={() => { setReplyTarget(note.id); setReplyText(note.reply || ""); }}
                      className="text-xs font-medium text-green-700 bg-green-50 border border-green-200 px-3 py-1.5 rounded-lg hover:bg-green-100 transition flex items-center gap-1">
                      <CornerDownLeft className="w-3.5 h-3.5" />
                      {note.reply ? t("notes_edit_reply") : t("notes_reply_btn")}
                    </button>
                    {/* Print Restock Form — only for restock notes */}
                    {note.client_name === "Restock Request" && (
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          try {
                            generateRestockPDF(note, companyData);
                          } catch (err) {
                            console.error("[RESTOCK PDF] Error:", err.message, err.stack);
                            alert("PDF Error: " + err.message);
                          }
                        }}
                        className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-lg hover:bg-amber-100 transition flex items-center gap-1">
                        <Printer className="w-3.5 h-3.5" />
                        {t("notes_print_restock")}
                      </button>
                    )}
                  </div>
                )}
              </div>

            </div>
          </div>
        );
      })}

      {/* Load More button — appends next 20 notes from Firestore */}
      {hasMore && (
        <div className="text-center py-4">
          <button
            onClick={() => loadNotes(false)}
            disabled={loadingMore}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-6 py-2 rounded-lg disabled:opacity-50">
            {loadingMore ? t("notes_loading_more") : t("notes_load_more_btn")}
          </button>
        </div>
      )}
      {!hasMore && notes.length > NOTES_PAGE_SIZE && (
        <p className="text-center text-xs text-gray-400 py-2">{t("notes_all_loaded")} ({notes.length})</p>
      )}
    </div>
  );
}

export default NotesInbox;
